import {
	PerformanceComparison,
	PerformancePeriod,
	ReceivableAgeBucket,
	useGetAdminReceivablesQuery,
	useGetPerformanceStatsQuery,
} from '@codezo/graphql';
import { cn } from '../../lib/utils';
import { useCurrencyStore } from '../../stores/currency';
import {
	compactMoney,
	cumulative,
	money,
	pctText,
	percentDelta,
	projectMonth,
	rangeLabel,
} from './performance/performanceFormat';

const LATE: ReceivableAgeBucket[] = [
	ReceivableAgeBucket.Late_1_30,
	ReceivableAgeBucket.Late_31Plus,
];

/**
 * The month so far, for the Overview: revenue against last month with a run-rate projection, margin,
 * orders and what is overdue. A summary of the Performance tab, which it links to — same queries, so
 * the two never disagree.
 */
export function ThisMonthCard({ onOpenPerformance }: { onOpenPerformance: () => void }) {
	const { selectedCurrency } = useCurrencyStore();
	const { data } = useGetPerformanceStatsQuery({
		variables: {
			period: PerformancePeriod.Month,
			compareTo: PerformanceComparison.PreviousPeriod,
			currency: selectedCurrency,
		},
		fetchPolicy: 'no-cache',
	});
	const { data: rData } = useGetAdminReceivablesQuery({
		variables: { currency: selectedCurrency },
		fetchPolicy: 'no-cache',
	});
	const stats = data?.getPerformanceStats;
	const receivables = rData?.getAdminReceivables;
	const cur = stats?.currency ?? selectedCurrency;
	const t = stats?.totals;
	const change = percentDelta(t?.revenue, stats?.comparisonTotals?.revenue);
	const overdue = receivables?.summary?.aging
		.filter((a) => LATE.includes(a.bucket))
		.reduce((s, a) => s + a.amount, 0);

	// Sparkline: this month's running total, last month's dashed, the projection dotted.
	const buckets = stats?.buckets ?? [];
	const n = buckets.length || 1;
	const firstFuture = buckets.findIndex((b) => b.future);
	const todayIndex = firstFuture === -1 ? n - 1 : Math.max(0, firstFuture - 1);
	const cum = cumulative(buckets);
	const projection = (cum[todayIndex] ?? 0) > 0 ? projectMonth(buckets) : null;
	const prevCum = stats?.comparisonBuckets ? cumulative(stats.comparisonBuckets) : null;
	const top =
		Math.max(
			projection?.[n - 1] ?? 0,
			cum[todayIndex] ?? 0,
			prevCum?.[prevCum.length - 1] ?? 0,
			1
		) * 1.05;
	const x = (i: number) => ((i + 0.5) / n) * 1000;
	const y = (v: number) => 100 - (v / top) * 100;
	const path = (vals: number[], from = 0) =>
		vals
			.map((v, k) => `${k ? 'L' : 'M'}${x(from + k).toFixed(1)} ${y(v).toFixed(2)}`)
			.join(' ');
	const actual = path(cum.slice(0, todayIndex + 1));
	const area = buckets.length
		? `${actual} L${x(todayIndex).toFixed(1)} 100 L${x(0).toFixed(1)} 100 Z`
		: '';

	const rows: { label: string; value: string; danger?: boolean }[] = [
		{ label: 'Gross margin', value: pctText(t?.marginPercent) },
		{ label: 'Orders confirmed', value: t ? String(t.orders) : '—' },
	];
	if (receivables && overdue != null) {
		rows.push({
			label: 'Overdue invoices',
			value: money(overdue, receivables.currency),
			danger: overdue > 0,
		});
	}

	return (
		<section
			className="@container overflow-hidden rounded-xl border border-line-1 bg-surface"
			aria-labelledby="month-h"
		>
			<div className="flex h-11 items-center gap-2 border-b border-line-1 px-4">
				<h2
					id="month-h"
					className="text-sm font-medium uppercase tracking-[0.04em] text-text-1"
				>
					This month
				</h2>
				{stats && (
					<span className="text-sm text-text-4 tabular-nums">
						{rangeLabel(stats.range.start, stats.range.end)}
					</span>
				)}
				<span className="flex-1" />
				<button
					type="button"
					onClick={onOpenPerformance}
					className="text-xs text-text-3 hover:text-text-1"
				>
					Performance →
				</button>
			</div>
			{/* Narrow (beside Activity): stacked. Wide (alone): figures, chart and rows side by side. */}
			<div className="grid @3xl:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_minmax(0,1fr)] @3xl:divide-x @3xl:divide-line-1">
				<div className="flex items-start justify-between gap-4 px-4 pt-4 @3xl:flex-col @3xl:justify-start @3xl:pb-4">
					<div className="flex flex-col gap-1.5">
						<span className="text-2xs font-medium uppercase tracking-[0.12em] text-text-4">
							Revenue to date
						</span>
						<span className="text-[28px] leading-none font-normal -tracking-[0.03em] text-text-1 tabular-nums">
							{t ? money(t.revenue, cur) : '—'}
						</span>
						<span className="text-xs text-text-3 tabular-nums">
							{change && (
								<span
									className={
										change.tone === 'down'
											? 'text-status-danger'
											: 'text-status-success'
									}
								>
									{change.text}
								</span>
							)}{' '}
							{stats?.comparisonRange
								? `vs ${rangeLabel(stats.comparisonRange.start, stats.comparisonRange.end)}`
								: ''}
						</span>
					</div>
					{projection && (
						<div className="flex flex-col items-end gap-1.5 @3xl:items-start">
							<span className="text-2xs font-medium uppercase tracking-[0.12em] text-text-4">
								Projected
							</span>
							<span className="text-base text-text-2 tabular-nums">
								{compactMoney(projection[n - 1], cur)}
							</span>
						</div>
					)}
				</div>
				<div className="px-4 pt-4 pb-3">
					<div className="relative h-[72px]">
						<svg
							aria-hidden="true"
							viewBox="0 0 1000 100"
							preserveAspectRatio="none"
							className="absolute inset-0 size-full overflow-visible"
						>
							{area && (
								<path d={area} fill="var(--color-accent)" fillOpacity="0.12" />
							)}
							{prevCum && (
								<path
									d={path(prevCum.slice(0, n))}
									fill="none"
									stroke="var(--color-text-3)"
									strokeWidth="1.25"
									strokeDasharray="3 3"
									vectorEffect="non-scaling-stroke"
								/>
							)}
							{projection && (
								<path
									d={path(projection.slice(todayIndex), todayIndex)}
									fill="none"
									stroke="var(--color-accent)"
									strokeWidth="1.5"
									strokeDasharray="1 4"
									strokeLinecap="round"
									vectorEffect="non-scaling-stroke"
								/>
							)}
							{actual && (
								<path
									d={actual}
									fill="none"
									stroke="var(--color-accent)"
									strokeWidth="1.75"
									vectorEffect="non-scaling-stroke"
								/>
							)}
						</svg>
						{buckets.length > 0 && (
							<span
								className="absolute -mb-[3.5px] -ml-[3.5px] size-[7px] rounded-full bg-accent shadow-[0_0_0_2px_var(--color-surface)]"
								style={{
									left: `${((todayIndex + 0.5) / n) * 100}%`,
									bottom: `${((cum[todayIndex] ?? 0) / top) * 100}%`,
								}}
							/>
						)}
					</div>
					<div className="mt-1.5 flex justify-between font-mono text-2xs text-text-4">
						<span>1</span>
						<span className="text-text-2">Today</span>
						<span>{n}</span>
					</div>
				</div>
				<div className="border-t border-line-1 @3xl:border-t-0">
					{rows.map((r, i) => (
						<div
							key={r.label}
							className={cn(
								'flex h-10 items-center justify-between px-4 text-base',
								i > 0 && 'border-t border-line-1'
							)}
						>
							<span className={r.danger ? 'text-status-danger' : 'text-text-2'}>
								{r.label}
							</span>
							<span
								className={cn(
									'tabular-nums',
									r.danger ? 'text-status-danger' : 'text-text-1'
								)}
							>
								{r.value}
							</span>
						</div>
					))}
				</div>
			</div>
		</section>
	);
}
